import { useCallback, useEffect, useState } from 'react';

import { type StudioTeamOrg, type StudioTeamPeoplePage, type StudioTeamPerson, adminClient } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminSection } from '@/components/shell/AdminSection';
import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableMessage, DataTableRow } from '@/components/table/DataTable';
import { SearchInput } from '@/components/table/SearchInput';
import { TablePager } from '@/components/table/TablePager';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatDateTime } from '@/lib/format';

import { OrgSwitcher } from '../Users/UsersPage';
import { pickOrg, personLabel, readRememberedOrg, rememberOrg } from '../Users/usersModel';
import { useAppNames } from './names';
import { type Check, type Grant, errorText, permissionsApi } from './permissionsApi';
import { BackLink, Problem, givenBy, objectText, untilText } from './shared';

const PAGE_SIZE = 25;
const COLUMNS = [{ label: 'Person' }, { label: 'Email', className: 'hidden md:table-cell' }];

export function PeopleTab({ open, onOpen }: { open?: string; onOpen: (id?: string) => void }) {
  const [orgs, setOrgs] = useState<StudioTeamOrg[]>([]);
  const [chosen, setChosen] = useState<string | undefined>(() => readRememberedOrg());
  const [page, setPage] = useState<StudioTeamPeoplePage>();
  const [error, setError] = useState<string>();
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const search = useDebouncedValue(query.trim(), 300);

  // The org of the page: the one remembered from the Users page, else the biggest, as that page picks it.
  const orgId = pickOrg(orgs, chosen);
  useEffect(() => {
    adminClient
      .getTeamAccess()
      .then((access) => setOrgs(access.orgs ?? []))
      .catch((failure) => setError(errorText(failure)));
  }, []);

  const load = useCallback(async () => {
    if (!orgId) return;
    setError(undefined);
    try {
      setPage(await adminClient.getTeamPeople({ orgId, q: search.length >= 2 ? search : undefined, access: 'everyone', page: Math.floor(offset / PAGE_SIZE) + 1, pageSize: PAGE_SIZE }));
    } catch (failure) {
      setError(errorText(failure));
    }
  }, [orgId, search, offset]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => setOffset(0), [search]);
  const onOrgChange = (next: string) => {
    rememberOrg(next);
    setChosen(next);
    setPage(undefined);
    setOffset(0);
  };

  if (open) return <PersonDetail email={open} onBack={() => onOpen()} />;

  return (
    <DataTable
      toolbar={
        <>
          <OrgSwitcher orgs={orgs} orgId={orgId} onChange={onOrgChange} />
          <SearchInput value={query} onChange={setQuery} placeholder="Search name or email" label="Search people" />
        </>
      }
      footer={page ? <TablePager total={page.total} limit={PAGE_SIZE} offset={offset} onOffsetChange={setOffset} /> : undefined}
    >
      <DataTableHead columns={COLUMNS} />
      {error ? (
        <DataTableLoadError colSpan={COLUMNS.length} message={error} onRetry={() => void load()} />
      ) : !page ? (
        <DataTableMessage colSpan={COLUMNS.length} loading>
          Loading people
        </DataTableMessage>
      ) : page.people.length === 0 ? (
        <DataTableMessage colSpan={COLUMNS.length}>No person matches.</DataTableMessage>
      ) : (
        <tbody>
          {page.people.map((person) => (
            <PersonRow key={person.userId} person={person} onOpen={onOpen} />
          ))}
        </tbody>
      )}
    </DataTable>
  );
}

function PersonRow({ person, onOpen }: { person: StudioTeamPerson; onOpen: (id?: string) => void }) {
  const email = person.email;
  return (
    <DataTableRow to={email ? `?tab=people&open=${encodeURIComponent(email)}` : undefined}>
      <DataTableCell>
        {email ? (
          <button type="button" className="font-medium text-foreground hover:underline" onClick={() => onOpen(email)}>
            {personLabel(person)}
          </button>
        ) : (
          personLabel(person)
        )}
      </DataTableCell>
      <DataTableCell className="hidden text-muted-foreground md:table-cell">{email ?? 'No email'}</DataTableCell>
    </DataTableRow>
  );
}

function PersonDetail({ email, onBack }: { email: string; onBack: () => void }) {
  const [grants, setGrants] = useState<Grant[]>();
  const [checks, setChecks] = useState<Check[]>();
  const [noPerson, setNoPerson] = useState(false);
  const [error, setError] = useState<string>();
  const [checkError, setCheckError] = useState<string>();
  const appName = useAppNames((checks ?? []).map((c) => c.app));

  const load = useCallback(async () => {
    setError(undefined);
    setCheckError(undefined);
    let list: Grant[];
    try {
      list = (await permissionsApi.grants('subject', email)).grants;
      setGrants(list);
    } catch (failure) {
      setError(errorText(failure));
      return;
    }
    // The checks need the person id. The grants carry it. A person with no grant has no checks to find by id.
    const ref = list.find((g) => g.subject.startsWith('user:') && g.subject !== 'user:*')?.subject;
    if (!ref) {
      setNoPerson(true);
      setChecks([]);
      return;
    }
    try {
      setChecks((await permissionsApi.checks({ subject: ref, limit: 50 })).checks);
    } catch (failure) {
      setCheckError(errorText(failure));
    }
  }, [email]);
  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6">
      <div>
        <BackLink label="All people" onClick={onBack} />
        <h2 className="text-lg font-semibold">{email}</h2>
      </div>
      <AdminSection title="Grants" description="What this person holds now." bodyClassName="p-0">
        {error ? <div className="p-4"><Problem message={error} onRetry={() => void load()} /></div> : null}
        {grants?.length === 0 ? <p className="p-5 text-sm text-muted-foreground">No live grants.</p> : null}
        {grants && grants.length > 0 ? (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
              <tr>
                {['Access', 'Relation', 'Given by', 'Until', 'Reason'].map((label) => (
                  <th key={label} scope="col" className="px-4 py-2 font-medium">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grants.map((grant) => (
                <tr key={grant.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2.5">{objectText(grant.object)}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{grant.relation}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{givenBy(grant)}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{untilText(grant.ends_at)}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{grant.reason || '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </AdminSection>
      <AdminSection title="Recent checks" description="What the service answered for this person, with the app that asked." bodyClassName="p-0">
        {checkError ? <div className="p-4"><Problem message={checkError} onRetry={() => void load()} /></div> : null}
        {noPerson ? <p className="p-5 text-sm text-muted-foreground">This person has no grant yet, so the service has no person id to show checks for.</p> : null}
        {checks?.length === 0 && !noPerson && !checkError ? <p className="p-5 text-sm text-muted-foreground">No recent checks.</p> : null}
        {checks && checks.length > 0 ? (
          <table className="w-full text-left text-sm">
            <thead className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
              <tr>
                {['When', 'App', 'Access', 'Answer', 'Reason'].map((label) => (
                  <th key={label} scope="col" className="px-4 py-2 font-medium">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {checks.map((check) => (
                <tr key={check.id} className="border-b border-border last:border-0">
                  <td className="px-4 py-2.5 text-muted-foreground">{formatDateTime(check.at)}</td>
                  <td className="px-4 py-2.5">{appName(check.app)}</td>
                  <td className="px-4 py-2.5">{objectText(`${check.resource.type}:${check.resource.id}`)}</td>
                  <td className="px-4 py-2.5">
                    <StatusPill tone={check.answer === 'allow' ? 'success' : 'danger'}>{check.answer === 'allow' ? 'Allow' : 'Deny'}</StatusPill>
                  </td>
                  <td className="px-4 py-2.5 text-muted-foreground">{check.reason || '–'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
      </AdminSection>
    </div>
  );
}
